import { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {
  fetchFilingBreakdownTimeline,
  TimelineEvent,
  TimePeriod,
  TimelinePeriod,
} from "../../lib/api/metrics";

export default function FilingBreakdown() {
  const [period, setPeriod] = useState<TimePeriod>("month");
  const [event, setEvent] = useState<TimelineEvent>("created");
  const [formTypes, setFormTypes] = useState<string[]>(["W-3A", "W-3"]);
  const [timeline, setTimeline] = useState<TimelinePeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const availableFormTypes = ["W-3A", "W-3", "W-2", "W-15", "GAU", "H-5"];

  useEffect(() => {
    const loadTimeline = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchFilingBreakdownTimeline(period, event, formTypes);
        setTimeline(data.timeline);
      } catch (err) {
        console.error("[FilingBreakdown] Error fetching timeline:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load filing breakdown"
        );
      } finally {
        setLoading(false);
      }
    };

    loadTimeline();
  }, [period, event, formTypes]);

  const handleFormTypeToggle = (formType: string) => {
    setFormTypes((prev) => {
      if (prev.includes(formType)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter((ft) => ft !== formType);
      } else {
        return [...prev, formType];
      }
    });
  };

  // Transform timeline data into chart series
  // Get all unique form types from timeline data
  const uniqueFormTypes = new Set<string>();
  timeline.forEach((item) => {
    item.breakdown.forEach((bd) => {
      uniqueFormTypes.add(bd.form_type);
    });
  });

  const series = Array.from(uniqueFormTypes).map((formType) => ({
    name: formType,
    data: timeline.map((period) => {
      const item = period.breakdown.find((bd) => bd.form_type === formType);
      return item ? item.count : 0;
    }),
  }));

  const options: ApexOptions = {
    colors: ["#c4320a", "#ec4a0a", "#fb6514", "#fd853a", "#feb273", "#fddcab"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "bar",
      stacked: true,
      height: 315,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "70%",
        borderRadius: 10,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: timeline.map((item) => item.date),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "left",
      fontFamily: "Outfit",
      fontSize: "14px",
      fontWeight: 400,
      markers: {
        size: 5,
        shape: "circle",
        strokeWidth: 0,
      },
      itemMargin: {
        horizontal: 10,
        vertical: 0,
      },
    },
    yaxis: {
      title: {
        text: "Count",
      },
    },
    grid: {
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      x: {
        show: true,
      },
      y: {
        formatter: (val: number) => `${val} filings`,
      },
    },
  };

  const eventOptions: { value: TimelineEvent; label: string }[] = [
    { value: "created", label: "Created" },
    { value: "submitted", label: "Submitted" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "rejected_and_submitted", label: "Rejected & Submitted" },
  ];

  const periodOptions: { value: TimePeriod; label: string }[] = [
    { value: "day", label: "Day" },
    { value: "week", label: "Week" },
    { value: "month", label: "Month" },
    { value: "quarter", label: "Quarter" },
    { value: "year", label: "Year" },
  ];

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-brand-500 border-r-transparent"></div>
          <p className="ml-3 text-sm text-gray-600 dark:text-gray-400">
            Loading breakdown...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
          <p className="text-sm font-medium text-error-700 dark:text-error-400">
            Failed to load breakdown: {error}
          </p>
        </div>
      </div>
    );
  }

  const totalFilings = timeline.reduce(
    (sum, item) => sum + item.breakdown.reduce((s, bd) => s + bd.count, 0),
    0
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      {/* Header with filters */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
            Filing Breakdown by Form Type
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Total: {totalFilings} filings
          </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
            {/* Event Filter */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Event:
              </label>
              <select
                value={event}
                onChange={(e) => setEvent(e.target.value as TimelineEvent)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              >
                {eventOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Period Filter */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                Period:
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as TimePeriod)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
              >
                {periodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Form Type Filter */}
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Form Types:
            </label>
            <div className="flex flex-wrap gap-2">
              {availableFormTypes.map((formType) => (
                <button
                  key={formType}
                  onClick={() => handleFormTypeToggle(formType)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    formTypes.includes(formType)
                      ? "bg-brand-500 text-white"
                      : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                  }`}
                >
                  {formType}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="overflow-x-auto custom-scrollbar pl-2">
        {timeline.length > 0 ? (
          <Chart
            className="-ml-5 min-w-[700px] xl:min-w-full"
            options={options}
            series={series}
            type="bar"
            height={315}
          />
        ) : (
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No data available for the selected period and event
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
